import React from 'react'
import {IIncomeDocument} from '../IncomeDocumentDetailPage'
import {Badge, Button, Col, Row} from 'react-bootstrap'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'
import {numberFormat} from '../../../utils/Functions'
import usePayment from '../../../hook/usePayment'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import {IncomeDocumentStateTypes, IncomeDocumentTypes} from '../../../constants/Common'
import {getRevenuePrefixPath} from '../../../utils/Helper'
import {
  groupPaymentsForDisplay,
  sumPaymentGroupAmount,
  sumPaymentGroupWhTaxPrice,
} from '../../../utils/paymentGrouping'
import {paymentEventFees, paymentEventTotal} from '../../../utils/paymentEvent'
import {invoiceReceiptAmounts} from '../../../utils/invoiceReceiptAmounts'

export const chequeRequiredKeys = [
  'chequeAccountNumber',
  'chequeBank',
  'chequeBranchNumber',
  'chequeDate',
  'chequeNumber',
]

interface ITabDocumentDetail {
  document: IIncomeDocument
  creditDocuments: IIncomeDocument[]
  onSetCurrentTab?(tab: string): void
}

const TabDocumentPayment: React.FC<ITabDocumentDetail> = (props) => {
  const {document, creditDocuments, onSetCurrentTab} = props
  const location = useLocation()
  const isInExpensePage = location.pathname.includes('expense')
  // console.log('TabDocumentPayment >>>>>>>>', {document})
  const navigate = useNavigate()
  const {getBankLabelByCode} = usePayment()
  const isExpenseDepositPayment = IncomeDocumentTypes.EXPENSE_DEPOSIT_PAYMENT === document.type
  const isExpensePurchaseInventoryRecord =
    IncomeDocumentTypes.EXPENSE_PURCHASE_INVENTORY_RECORD === document.type
  const isCombined = IncomeDocumentTypes.EXPENSE_COMBINED_PAYMENT_NOTE === document.type
  if (!document) return <></>

  const isCreditDebit = [
    IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE,
    IncomeDocumentTypes.EXPENSE_PURCHASE_DEBIT_NOTE,
  ].includes(document?.type)

  const combinedWhTaxPriceTotal = document.billingHistories.reduce(
    (a, b) =>
      a + (b?.incomeDocument?.type === 'expense-combined-payment-note' ? Number(b?.whTaxPrice) : 0),
    0
  )

  const incomeDocumentPaymentDeductions = document?.incomeDocumentPaymentDeductions?.filter(
    (item) => !item?.isCombined && item?.isUseForPaid
  )

  const linkingDocument = document?.linkingDocument
  const isDocumentCredit = document.type === IncomeDocumentTypes.CREDIT
  const invoiceAmounts = invoiceReceiptAmounts(document)
  const invoiceFullPaymentAmount =
    document.type === IncomeDocumentTypes.INVOICE
      ? invoiceAmounts.grossAmount
      : document.totalAmount
  const totalNetAmountReceivePayment = isCombined
    ? document.netAmount
    : document.type === IncomeDocumentTypes.INVOICE
      ? invoiceAmounts.settlementAmount + combinedWhTaxPriceTotal
      : invoiceFullPaymentAmount - document.whTaxPrice + combinedWhTaxPriceTotal

  const incomeDocumentDepositDeductionAppliers =
    document?.incomeDocumentDepositDeductionAppliers || []

  const mappedDepositDeductionAppliers = incomeDocumentDepositDeductionAppliers.flatMap((item) => {
    const payments = item?.incomeDocument?.incomeDocumentPayments || []

    return payments.filter((payment: any) => !payment?.cancelledAt && !payment?.cancelled_at).map((payment: any) => ({
      id: payment.id,
      date: payment.date,
      code: item?.incomeDocument?.code,
      type: item?.incomeDocument?.type,
      bank: payment.bank,
      accountNo: payment.accountNo,
      accountName: payment.accountName,
      amount: Number(item?.amount || 0) - Number(item?.whTaxPrice || 0),
      note: payment.note,
      incomeDocumentId: payment.incomeDocumentId,
      createdAt: item?.createdAt,
      updatedAt: item?.updatedAt,
      whTax: payment.whTax,
      whTaxPrice: Number(item.whTaxPrice),
      chequeNumber: payment.chequeNumber,
      chequeBranchNumber: payment.chequeBranchNumber,
      chequeBank: payment.chequeBank,
      chequeAccountNumber: payment.chequeAccountNumber,
      chequeDate: payment.chequeDate,
      receiptId: payment.receiptId,
      pndType: payment.pndType,
      taxCondition: payment.taxCondition,
      gatewayId: payment.gatewayId,
      wthType: payment.whTaxPrice,
      withholdingTaxItems: payment.withholdingTaxItems || [],
      receipt: payment.receipt || null,
    }))
  })
  const incomeDocumentPayments = [
    ...(document?.incomeDocumentPayments || []).filter(
      (payment: any) => !payment?.cancelledAt && !payment?.cancelled_at
    ),
    ...([
      IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE,
      IncomeDocumentTypes.EXPENSE_PURCHASE_DEBIT_NOTE,
    ].includes(document.type)
      ? mappedDepositDeductionAppliers
      : []),
  ]

  // `parentPaymentId` groups bank channels created in one submit. The child
  // rows retain their own amounts and continue to be included by every
  // payment/accounting calculation above; this is display grouping only.
  const paymentGroups = groupPaymentsForDisplay(incomeDocumentPayments)

  const netAwaitPaymentReceiveLength = paymentGroups.length || 0
  const paymentFeeIncreaseCode = document.incomeDocumentPaymentFees
    .filter(item => item.reason === "-1")
    .map(item => item.accountCode)
    .join(',')

  const paymentFeeDecreaseCode = document.incomeDocumentPaymentFees
    .filter(item => item.reason === "1")
    .map(item => item.accountCode)
    .join(',')

  const paymentFeeIncrease = document.incomeDocumentPaymentFees.reduce(
    (a, b) => (b.reason === '-1' ? a : a + b.amount),
    0
  )
  const paymentFeeDecrease = document.incomeDocumentPaymentFees.reduce(
    (a, b) => (b.reason === '1' ? a : a + b.amount),
    0
  )

  const isNewExpense = [
    IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE,
    IncomeDocumentTypes.EXPENSE_PURCHASE_DEBIT_NOTE,
    IncomeDocumentTypes.EXPENSE_COMBINED_PAYMENT_NOTE,
    IncomeDocumentTypes.EXPENSE_EXPENSE_RECORD,
    IncomeDocumentTypes.EXPENSE_PURCHASE_INVENTORY_RECORD,
    IncomeDocumentTypes.EXPENSE_DEPOSIT_PAYMENT,
  ].includes(document.type)

  const childrenDocuments =
    document.type === IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE ||
    document.type === IncomeDocumentTypes.EXPENSE_PURCHASE_DEBIT_NOTE
      ? document.childrenDocuments
      : null

  let totalWhTax = 0
  if (
    incomeDocumentPayments &&
    incomeDocumentPayments.length > 0 &&
    incomeDocumentPayments[0].extraWithholdingTaxItems &&
    incomeDocumentPayments[0].extraWithholdingTaxItems.length > 0
  ) {
    for (let i = 0; i < incomeDocumentPayments[0].extraWithholdingTaxItems.length; i++) {
      totalWhTax = totalWhTax + incomeDocumentPayments[0].extraWithholdingTaxItems[i].whTaxPrice
    }
  }
  let totalItemsWhTax = 0
  if (document?.incomeDocumentItems && document?.incomeDocumentItems.length > 0) {
    for (let i = 0; i < document?.incomeDocumentItems.length; i++) {
      totalItemsWhTax = totalItemsWhTax + document?.incomeDocumentItems[i].whTaxPrice
    }
  }

  let totalPaidWhTaxPrice = !!document.incomeDocumentDepositDeductions.length
    ? document.whTaxPrice
    : incomeDocumentPayments.reduce((total: number, payment: any) => {
        return total + (isCombined ? Number(payment?.whTaxPrice || 0) : 0)
      }, 0)

  // console.log({totalPaidWhTaxPrice})

  if (isCreditDebit) {
    totalPaidWhTaxPrice += document.whTaxPrice
  }

  // totalPaidWhTaxPrice += document.incomeDocumentItems.reduce((total: number, payment: any) => {
  //   return total + Number(payment.whTaxPrice || 0)
  // }, 0)

  // console.log('12424fsasafsf', {totalPaidWhTaxPrice})
  // console.log('12424fsasafsf', {totalWhTax})
  if (
    !isCreditDebit &&
    typeof totalItemsWhTax === 'number' &&
    !!incomeDocumentPayments?.length &&
    !document.incomeDocumentDepositDeductions.length
  ) {
    if (!isCombined) {
      totalPaidWhTaxPrice += totalItemsWhTax
    } else {
      // totalPaidWhTaxPrice += document?.whTaxPrice || 0
    }
  }

  let netAwaitPaymentReceiveAmount = incomeDocumentPayments.reduce((a, b) => {
    return (
      a + Number((Number(b.amount) + (isCreditDebit ? Number(b?.whTaxPrice || 0) : 0)).toFixed(2))
    )
  }, 0)
  // console.log('Step1 reduce:', netAwaitPaymentReceiveAmount)

  if (totalPaidWhTaxPrice && !isCreditDebit && !document.incomeDocumentDepositDeductions.length) {
    netAwaitPaymentReceiveAmount += isCombined ? 0 : totalPaidWhTaxPrice
  }
  // console.log('Step2 +whTax:', netAwaitPaymentReceiveAmount, totalPaidWhTaxPrice)

  let paymentDeductionsAmount = 0
  let paymentDeductionsRemainAmount = 0
  let paymentDeductionsWhTaxPrice = 0

  for (let i = 0; i < incomeDocumentPaymentDeductions.length; i++) {
    const currentPaymentDeductions = incomeDocumentPaymentDeductions[i]
    if (currentPaymentDeductions.type === 'expense-purchase-debit-note') {
      paymentDeductionsAmount -= Number(currentPaymentDeductions?.amount || 0)
      paymentDeductionsRemainAmount -= Number((currentPaymentDeductions?.amount || 0) - (currentPaymentDeductions?.currentDoc?.whTaxPrice || 0))
      // totalPaidWhTaxPrice += Math.abs(Number(currentPaymentDeductions?.whTaxPrice || 0))
      paymentDeductionsWhTaxPrice += Number(currentPaymentDeductions?.currentDoc?.whTaxPrice || 0)
    } else {
      paymentDeductionsAmount += Number(currentPaymentDeductions?.amount || 0)
      paymentDeductionsRemainAmount += Number(currentPaymentDeductions?.amount || 0)
      totalPaidWhTaxPrice -= Math.abs(Number(currentPaymentDeductions?.whTaxPrice || 0))
      paymentDeductionsWhTaxPrice -= Number(currentPaymentDeductions?.currentDoc?.whTaxPrice || 0)
    }
  }

  let childrenDocumentsAmount = 0

  if (childrenDocuments && !isCreditDebit) {
    for (let i = 0; i < childrenDocuments.length; i++) {
      const currentDeductions = childrenDocuments[i]
      paymentDeductionsAmount += Number(currentDeductions?.linkingDocument?.totalAmount || 0)
    }
  }

  const totalCreditAmount = creditDocuments.reduce((a, b) => a + b.totalAmount, 0)
  const billingHistories = document.billingHistories
  const billingNoteTotalAmount = document.billingHistories.reduce(
    (a, b) =>
      a +
      (b.billingAmount -
        (['expense-combined-payment-note'].includes(b?.incomeDocument?.type) ? 0 : b.whTaxPrice)),
    0
  )
  let billingNoteAlreadyPaidTotal = document.billingHistories.reduce(
    (a, b) =>
      b.incomeDocument.state === 'paid'
        ? a +
          (b.billingAmount -
            (isNewExpense
              ? 0
              : ['expense-combined-payment-note'].includes(b?.incomeDocument?.type)
              ? 0
              : b.whTaxPrice))
        : a,
    0
  )

  if (isNewExpense) {
    billingNoteAlreadyPaidTotal -= isCombined ? 0 : document?.whTaxPrice || 0
  }

  const isExpense = document.type.includes('expense')
  // console.log({billingNoteAlreadyPaidTotal})
  // console.log({paymentDeductionsAmount})
  // console.log({paymentFeeDecrease})
  // console.log({paymentFeeIncrease})

  let paymentReceivedLength =
    netAwaitPaymentReceiveLength + (!isNewExpense ? billingHistories.length || 0 : 0)
  let paymentReceivedAmount =
    netAwaitPaymentReceiveAmount + billingNoteAlreadyPaidTotal + paymentDeductionsAmount

  netAwaitPaymentReceiveAmount += paymentFeeDecrease - paymentFeeIncrease
  // console.log('Step3 +fee:', paymentFeeDecrease, paymentFeeIncrease, netAwaitPaymentReceiveAmount)

  if (document.type === 'credit') {
    if (linkingDocument?.state === 'paid') {
      const linkingIncomeDocumentPaymentDeductions =
        linkingDocument?.incomeDocumentPaymentDeductions?.filter((item: any) => !item?.isCombined)
      paymentReceivedLength = linkingIncomeDocumentPaymentDeductions?.length || 0
      paymentReceivedAmount = linkingIncomeDocumentPaymentDeductions.reduce(
        (a: any, b: any) => a + b.amount,
        0
      )
    }
  }

  const paymentReceivedFromPayments = paymentEventTotal(
    incomeDocumentPayments,
    document.incomeDocumentPaymentFees || []
  )

  // console.log(33333333333333)
  // console.log({totalNetAmountReceivePayment})
  // console.log({netAwaitPaymentReceiveAmount})
  // console.log({totalCreditAmount})
  // console.log({billingNoteAlreadyPaidTotal})
  // console.log({paymentDeductionsAmount})
  // console.log(33333333333333)

  const isFullPaid =
    totalNetAmountReceivePayment -
      combinedWhTaxPriceTotal -
      netAwaitPaymentReceiveAmount -
      totalCreditAmount -
      billingNoteAlreadyPaidTotal -
      paymentDeductionsRemainAmount <=
    0

  const isUseWhTaxItems = isNewExpense && !incomeDocumentPayments?.length && document?.whTaxPrice

  const _renderPayCheque = (paymentItem: any) => {
    return (
      <div>
        <Badge bg={'secondary'} className={'px-3'}>
          {'เช็ค : Cheque'}
        </Badge>
        <Row>
          <Col xs={6}>
            <p className={'mb-0'}>{`เลขที่เช็ค : ${paymentItem.chequeNumber}`}</p>
          </Col>
          <Col xs={6}>
            <p className={'mb-0'}>{`เลขที่สาขา : ${paymentItem.chequeBranchNumber}`}</p>
          </Col>
          <Col xs={6}>
            <p className={'mb-0'}>{`เลขที่บัญชีในเช็ค : ${paymentItem.chequeAccountNumber}`}</p>
          </Col>
          <Col xs={6}>
            <p className={'mb-0'}>{`ธนาคารที่ระบุไว้ : ${getBankLabelByCode(
              paymentItem.chequeBank
            )}`}</p>
          </Col>
          <Col xs={6}>
            <p className={'mb-0'}>{`วันที่ในเช็ค : ${paymentItem.chequeDate}`}</p>
          </Col>
        </Row>
      </div>
    )
  }

  const _renderPayBankAccount = (paymentItem: any) => {
    return (
      <div>
        <Row>
          <Col xs={6}>
            <p className={'mb-0'}>{`ธนาคาร : ${getBankLabelByCode(paymentItem.bank)}`}</p>
          </Col>
          <Col xs={6}>
            <p className={'mb-0'}>{`เลขที่บัญชี : ${paymentItem.accountNo}`}</p>
          </Col>
          <Col xs={6}>
            <p className={'mb-0'}>{`ชื่อบัญชี : ${paymentItem.accountName}`}</p>
          </Col>
        </Row>
      </div>
    )
  }

  const fullPaymentAmountLabel = () => {
    if (document.type === 'invoice') return 'มูลค่าเต็มใบแจ้งหนี้'
    if (document.type === 'credit') return 'มูลค่าเต็มใบลดหนี้'
    if (document.type === 'debit') return 'มูลค่าเต็มใบเพิ่มหนี้'
    return 'มูลค่าเต็มของเอกสาร'
  }

  let isFullyPaid =
    Number(
      (
        totalNetAmountReceivePayment -
        netAwaitPaymentReceiveAmount -
        totalCreditAmount -
        billingNoteAlreadyPaidTotal
      ).toFixed(2)
    ) === 0

  if (document.type === IncomeDocumentTypes.DEPOSIT) {
    isFullyPaid =
      Number(
        (
          totalNetAmountReceivePayment -
          netAwaitPaymentReceiveAmount -
          billingNoteAlreadyPaidTotal
        ).toFixed(2)
      ) === 0
  }

  if (['credit', 'debit'].includes(document.type)) {
    if (
      document?.linkingDocument?.type === 'invoice' &&
      document?.linkingDocument?.state === 'paid'
    ) {
      isFullyPaid = true
    }
  }

  const awaitReceivePayment =
    totalNetAmountReceivePayment -
    netAwaitPaymentReceiveAmount -
    totalCreditAmount -
    billingNoteAlreadyPaidTotal

  // console.log(2222222222)
  // console.log({totalNetAmountReceivePayment})
  // console.log({netAwaitPaymentReceiveAmount})
  // console.log({totalCreditAmount})
  // console.log({billingNoteAlreadyPaidTotal})
  // console.log({paymentDeductionsAmount})
  // console.log(
  //   'incomeDocumentPaymentDeductions.length >>>',
  //   incomeDocumentPaymentDeductions.length
  // )
  // console.log(2222222222)

  const paymentDeductionsLength = isNewExpense
    ? incomeDocumentPaymentDeductions.length
    : creditDocuments.length ||
      incomeDocumentPaymentDeductions.length ||
      (childrenDocuments && childrenDocuments?.length)

  const paymentDeductionsTotalAmount = isNewExpense
    ? paymentDeductionsAmount
    : totalCreditAmount || paymentDeductionsAmount || (childrenDocuments && childrenDocumentsAmount)
  return (
    <KTCard>
      <KTCardBody className={'py-3 px-4'}>
        <Row className={'fs-6'}>
          <Col xs={12}>
            <h4>{'ข้อมูลการชำระ'}</h4>
          </Col>
          <Col md={6} className={'border-end'}>
            <div className={'d-flex justify-content-between'}>
              <strong>{`${fullPaymentAmountLabel()} :`}</strong>
              <span>{`${numberFormat(invoiceFullPaymentAmount)} บาท`}</span>
            </div>
            {billingHistories.length > 0 && (
              <div className={'d-flex justify-content-between'}>
                <div>
                  <strong>
                    {isExpense ? `มูลค่าที่นำไปจ่ายในใบรวม` : `มูลค่าที่นำไปวางบิลในใบวางบิล`}
                  </strong>
                  <strong
                    className={'text-info cursor-pointer ms-1'}
                    onClick={() => onSetCurrentTab?.('billingNote')}
                  >{`${billingHistories.length} รายการ:`}</strong>
                </div>
                <span>{`${numberFormat(billingNoteTotalAmount)} บาท`}</span>
              </div>
            )}
            <div className={'d-flex justify-content-between'}>
              <strong>{`มูลค่าสุทธิที่ต้อง${isExpense ? 'จ่าย' : 'รับ'}ชำระทั้งสิ้น :`}</strong>
              <span style={{color: isDocumentCredit ? '#6c6df2' : '#43bf76'}}>
                {`${numberFormat(totalNetAmountReceivePayment - billingNoteTotalAmount)} บาท`}
              </span>
            </div>
            <div className={'d-flex justify-content-between'}>
              <strong>{`มูลค่าที่รับชำระแล้ว ${paymentReceivedLength} รายการ :`}</strong>
              <span>{`${numberFormat(paymentReceivedFromPayments)} บาท`}</span>
            </div>
            {(isNewExpense
              ? incomeDocumentPaymentDeductions.length > 0
              : creditDocuments.length > 0 ||
                incomeDocumentPaymentDeductions.length > 0 ||
                (childrenDocuments && childrenDocuments.length > 0)) && (
              <div className={'d-flex justify-content-between'}>
                <strong>{`มูลค่าตัดชำระกับเอกสาร ${paymentDeductionsLength} รายการ :`}</strong>
                <span>{`${
                  (paymentDeductionsTotalAmount || 0) < 0
                    ? `(${numberFormat(Math.abs(paymentDeductionsTotalAmount || 0))})`
                    : numberFormat(paymentDeductionsTotalAmount)
                } บาท`}</span>
              </div>
            )}
            {/* {!!isUseWhTaxItems && (
              <div className={'d-flex justify-content-between'}>
                <strong>{`มูลค่าภาษีหัก ณ ที่จ่ายจากรายการ:`}</strong>
                <span>{`${numberFormat(
                  document?.whTaxPrice +
                    (!incomeDocumentPayments?.length &&
                    typeof paymentDeductionsWhTaxPrice === 'number'
                      ? paymentDeductionsWhTaxPrice
                      : 0)
                )} บาท`}</span>
              </div>
            )} */}
          </Col>
          <Col md={6}>
            {isFullPaid || isFullyPaid ? (
              <div
                className={'fs-3 fw-bold text-end'}
                style={{color: isDocumentCredit ? '#6c6df2' : '#43bf76'}}
              >
                {isNewExpense ? 'จ่ายชำระเงินเต็มจำนวนแล้ว' : 'รับชำระเงินเต็มจำนวนแล้ว'}
              </div>
            ) : (
              <div className={'d-flex justify-content-between'}>
                <span>{'มูลค่าที่รอรับชำระเงินอีก :'}</span>
                <div>
                  <div
                    className={'py-2 px-3 text-end'}
                    style={{minWidth: 150, background: '#ececff', color: '#6c6df2'}}
                  >
                    <strong className={'fs-4'}>
                      {isFullPaid
                        ? 0
                        : numberFormat(
                            totalNetAmountReceivePayment -
                              netAwaitPaymentReceiveAmount -
                              totalCreditAmount -
                              billingNoteAlreadyPaidTotal -
                              paymentDeductionsRemainAmount
                          )}
                    </strong>
                    <span className={'ms-2'}>{'บาท'}</span>
                  </div>
                  <div>
                    <span>{'วันที่ครบกำหนด :'}</span>
                    <strong className={'ms-2'}>{document.expiryDate}</strong>
                  </div>
                  <div></div>
                </div>
              </div>
            )}
          </Col>
        </Row>
        <hr />
        {incomeDocumentPaymentDeductions.length > 0 && (
          <>
            {incomeDocumentPaymentDeductions?.map((doc, index) => {
              const {code, amount, currentDocId, type} = doc

              const pathPrefix = getRevenuePrefixPath(type)
              return (
                <KTCard className={'mt-3'} key={`deposit-deduction-${index}`}>
                  <KTCardBody className={'py-3 px-4'}>
                    <Row className={'fs-6'}>
                      <Col xs={12}>
                        <h4 style={{color: '#43bf76'}}>{`ตัดชำระกับเอกสาร ใบที่ ${index + 1}`}</h4>
                      </Col>
                      <Col md={6} className={'border-end'}>
                        <div className={'d-flex gap-2'}>
                          <strong>{'เลขที่เอกสาร :'}</strong>
                          <Link to={`${pathPrefix}/${currentDocId}/detail`}>{code}</Link>
                        </div>
                        <div className={'d-flex justify-content-between'}>
                          <span>{'จำนวนยอด :'}</span>
                          <span>{`${numberFormat(amount)} บาท`}</span>
                        </div>
                      </Col>
                    </Row>
                  </KTCardBody>
                </KTCard>
              )
            })}
            <hr />
          </>
        )}
        {!isNewExpense && !!childrenDocuments && !!childrenDocuments?.length && (
          <>
            {childrenDocuments?.map((doc, index) => {
              const {currentDocCode, currentDocId, type} = doc
              const {totalAmount} = doc.linkingDocument

              const pathPrefix = getRevenuePrefixPath(type)
              return (
                <KTCard className={'mt-3'} key={`deposit-deduction-${index}`}>
                  <KTCardBody className={'py-3 px-4'}>
                    <Row className={'fs-6'}>
                      <Col xs={12}>
                        <h4 style={{color: '#43bf76'}}>{`ตัดชำระกับเอกสาร ใบที่ ${index + 1}`}</h4>
                      </Col>
                      <Col md={6} className={'border-end'}>
                        <div className={'d-flex gap-2'}>
                          <strong>{'เลขที่เอกสาร :'}</strong>
                          <Link to={`${pathPrefix}/${currentDocId}/detail`}>{currentDocCode}</Link>
                        </div>
                        <div className={'d-flex justify-content-between'}>
                          <span>{'จำนวนยอด :'}</span>
                          <span>{`${numberFormat(totalAmount)} บาท`}</span>
                        </div>
                      </Col>
                    </Row>
                  </KTCardBody>
                </KTCard>
              )
            })}
            <hr />
          </>
        )}
        <Row className={'fs-6'}>
          {paymentGroups.map(({payment: item, children}: any, index: number) => {
            const receiptDocument = item?.receipt
            const alreadyHasReceipt =
              document.type === IncomeDocumentTypes.RECEIPT || receiptDocument
            const isCheque = chequeRequiredKeys.some((key) => key in item && item[key] !== null)
            const paymentGroupAmount = sumPaymentGroupAmount(item, children)
            const paymentGroupWhTaxPrice = sumPaymentGroupWhTaxPrice(item, children)
            const paymentFees = paymentEventFees(document.incomeDocumentPaymentFees || [], [item, ...children])
            const paymentFeeDeduct = paymentFees
              .filter((fee: any) => Number(fee.reason) === -1)
              .reduce((total: number, fee: any) => total + Number(fee.amount || 0), 0)
            const paymentFeeAdd = paymentFees
              .filter((fee: any) => Number(fee.reason) === 1)
              .reduce((total: number, fee: any) => total + Number(fee.amount || 0), 0)
            const paymentFeeDeductCodes = paymentFees
              .filter((fee: any) => Number(fee.reason) === -1)
              .map((fee: any) => fee.accountCode)
              .join(',')
            const paymentFeeAddCodes = paymentFees
              .filter((fee: any) => Number(fee.reason) === 1)
              .map((fee: any) => fee.accountCode)
              .join(',')

            return (
              <Col xs={12}>
                <Row>
                  <Col xs={12}>
                    <div className='d-flex justify-content-between gap-2'>
                      <h4 style={{color: isDocumentCredit ? '#6c6df2' : '#43bf76'}}>{`${
                        isExpense ? 'จ่าย' : 'รับ'
                      }ชำระเงินครั้งที่ ${index + 1}`}</h4>
                      <div>
                        {`จ่ายชำระเมื่อวันที่: ${
                          item.date
                            ? new Date(item.date).toLocaleDateString('th-TH', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })
                            : ''
                        }`}
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className={'border-end'}>
                    {![
                      IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE,
                      IncomeDocumentTypes.EXPENSE_PURCHASE_DEBIT_NOTE,
                    ].includes(document.type) && (
                      <>
                        <strong className={'fs-5'}>{'ช่องทางการชำระ'}</strong>
                        {isCheque ? _renderPayCheque(item) : _renderPayBankAccount(item)}
                        {children.map((child: any) => {
                          const childIsCheque = chequeRequiredKeys.some(
                            (key) => key in child && child[key] !== null
                          )
                          return (
                            <div className={'mt-3 ms-4'} key={`payment-channel-${child.id}`}>
                              <strong className={'fs-6'}>{'ช่องทางการชำระเพิ่มเติม'}</strong>
                              {childIsCheque ? _renderPayCheque(child) : _renderPayBankAccount(child)}
                              <div className={'d-flex justify-content-between'}>
                                <strong>{'จำนวนเงิน :'}</strong>
                                {`${numberFormat(child.amount)} บาท`}
                              </div>
                            </div>
                          )
                        })}
                      </>
                    )}
                    <div className={'d-flex justify-content-between'}>
                      <strong>{'จำนวนเงิน :'}</strong>
                      {`${numberFormat(item.amount)} บาท`}
                    </div>
                    {!isNewExpense ? (
                      <>
                        {index === 0 && (
                          <div className={'d-flex justify-content-between'}>
                            <strong>{'ภาษีหัก ณ ที่จ่าย :'}</strong>
                            {`${numberFormat(totalPaidWhTaxPrice)} บาท`}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className={'d-flex justify-content-between'}>
                        <strong>{'ภาษีหัก ณ ที่จ่าย :'}</strong>
                        {`${numberFormat(item?.whTaxPrice || 0)} บาท`}
                      </div>
                    )}
                    {item?.code && (
                      <div className={'d-flex gap-2'}>
                        <strong>{'ตัดชำระกับเอกสาร :'}</strong>
                        <Link
                          to={`${getRevenuePrefixPath(item?.type)}/${
                            item?.incomeDocumentId
                          }/detail`}
                          className={'fw-bold'}
                          target={'_blank'}
                        >
                          {item?.code}
                        </Link>
                      </div>
                    )}
                    {item?.accountBooks && item.accountBooks.length > 0 && (
                      <div className={'mt-2'}>
                        <strong className={'fs-6'}>{'รายการบัญชี :'}</strong>
                        {item.accountBooks.map((accountBook: any, abIndex: number) =>
                          accountBook?.code ? (
                            <div key={`ab-${abIndex}`} className={'d-flex gap-2 ms-2'}>
                              <span>{'เอกสารอ้างอิง :'}</span>
                              <a
                                href={`/accountant/dailyJournalDetail/${accountBook.id}`}
                                target={'_blank'}
                                rel={'noopener noreferrer'}
                                className={'fw-bold'}
                              >
                                {accountBook.code}
                              </a>
                            </div>
                          ) : null
                        )}
                      </div>
                    )}
                    {!!(paymentFeeDeduct || paymentFeeAdd) && (
                      <>
                        <strong className={'fs-5'}>{'ค่าธรรมเนียม หรือรายการปรับปรุง'}</strong>
                        <div className={'d-flex justify-content-between'}>
                          <div className={'d-flex gap-1'}>
                            <span style={{color: '#f47459'}}>หักเงินที่ได้รับ</span>
                            <span>ด้วยบัญชี {paymentFeeDeductCodes && `- ${paymentFeeDeductCodes}`}</span>
                          </div>
                          <span>{`${numberFormat(paymentFeeDeduct)} บาท`}</span>
                        </div>
                        <div className={'d-flex justify-content-between'}>
                          <div className={'d-flex gap-1'}>
                            <span style={{color: '#43bf76'}}>เพิ่มเงินที่ได้รับ</span>
                            <span>ด้วยบัญชี {paymentFeeAddCodes && `- ${paymentFeeAddCodes}`}</span>
                          </div>
                          <span>{`${numberFormat(paymentFeeAdd)} บาท`}</span>
                        </div>
                      </>
                    )}
                  </Col>
                  <Col md={6}>
                    <div className={'d-flex justify-content-between'}>
                      <strong>{'มูลค่าที่รับชำระเงินรวม :'}</strong>
                      <div
                        className={'py-2 px-3 text-end'}
                        style={{
                          minWidth: 150,
                          background: '#f5fcf8',
                          color: isDocumentCredit ? '#6c6df2' : '#43bf76',
                        }}
                      >
                        {!isNewExpense ? (
                          <strong className={'fs-4'}>
                            {numberFormat(
                              paymentGroupAmount +
                                paymentGroupWhTaxPrice + paymentFeeDeduct - paymentFeeAdd
                            )}
                          </strong>
                        ) : (
                          <strong className={'fs-4'}>
                            {numberFormat(paymentGroupAmount + paymentGroupWhTaxPrice)}
                          </strong>
                        )}

                        <span className={'ms-2'}>{'บาท'}</span>
                      </div>
                    </div>
                    {isNewExpense ? (
                      <>
                        {(document.type === IncomeDocumentTypes.EXPENSE_PURCHASE_INVENTORY_RECORD ||
                          document.type === IncomeDocumentTypes.EXPENSE_EXPENSE_RECORD) &&
                          document.state === 'receive-receipt' && (
                            <div
                              className={'d-flex justify-content-between align-items-center mt-3'}
                            >
                              <strong className={'fs-2'} style={{color: '#43bf76'}}>
                                {isInExpensePage ? 'รับใบเสร็จแล้ว' : 'ออกใบเสร็จแล้ว'}
                              </strong>
                            </div>
                          )}
                      </>
                    ) : (
                      <>
                        {(document.type !== IncomeDocumentTypes.DEPOSIT ||
                          isExpenseDepositPayment ||
                          isExpensePurchaseInventoryRecord) && (
                          <div className={'d-flex justify-content-between align-items-center mt-3'}>
                            <strong
                              className={'fs-2'}
                              style={{color: alreadyHasReceipt ? '#43bf76' : '#f47459'}}
                            >
                              {alreadyHasReceipt
                                ? isInExpensePage
                                  ? 'รับใบเสร็จแล้ว'
                                  : 'ออกใบเสร็จแล้ว'
                                : isInExpensePage
                                ? 'รอรับใบเสร็จ'
                                : 'รอออกใบเสร็จ'}
                            </strong>
                            {!alreadyHasReceipt ? (
                              <Button
                                variant={'info'}
                                onClick={() => {
                                  navigate(
                                    `/revenue-receipt/create?docId=${document.id}&paymentId=${
                                      item.id
                                    }&type=${document?.type || ''}`
                                  )
                                }}
                              >
                                {isInExpensePage ? 'รับใบเสร็จ' : 'ออกใบเสร็จ'}
                              </Button>
                            ) : (
                              <span>
                                {'เลขที่ใบเสร็จ :'}
                                <Link
                                  to={`/revenue-receipt/${
                                    receiptDocument ? receiptDocument.id : document.id
                                  }/detail?type=${document?.type || ''}`}
                                  target={'_blank'}
                                  className={'ms-1 fw-bold'}
                                >
                                  {receiptDocument ? receiptDocument.code : document.code}
                                </Link>
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </Col>
                </Row>
                <hr />
              </Col>
            )
          })}
        </Row>
      </KTCardBody>
    </KTCard>
  )
}

export default TabDocumentPayment
